import {
  CONNECTION_TIMEOUT_MS,
  HEARTBEAT_INTERVAL_MS,
  PROTOCOL_VERSION,
  STATE_SEND_HZ,
  WORLD_CLOCK_PERIOD_S,
  type ClientMessage,
  type NetAppearance,
  type NetPlayer,
  type NpcTuple,
  type ServerMessage,
} from './protocol';

export type ConnectionStatus = 'offline' | 'connecting' | 'online' | 'error';

export interface NetworkEvents {
  onStatus?: (status: ConnectionStatus, detail?: string) => void;
  onWelcome?: (
    selfId: string,
    players: NetPlayer[],
    resume?: { p: [number, number, number]; r: number },
  ) => void;
  /** Server odmítl pozici a vrací hráče zpět. */
  onCorrection?: (p: [number, number, number], reason: string) => void;
  onSpawn?: (player: NetPlayer) => void;
  onLeave?: (id: string) => void;
  onState?: (id: string, p: [number, number, number], r: number, a: number, receivedAt: number) => void;
  onLook?: (id: string, appearance: NetAppearance) => void;
  onChat?: (id: string, name: string, message: string) => void;
  /** Kde má vzdálený hráč nahozeno; null = přestal rybařit. */
  onSpot?: (id: string, p: [number, number] | null) => void;
  /** Stav nepřátel od hráče, který je simuluje. */
  onNpcState?: (from: string, state: NpcTuple[]) => void;
  /** Někdo zasáhl nepřítele. */
  onHit?: (id: number, damage: number, by: string) => void;
  /** Někdo zasáhl nás. */
  onPlayerDamage?: (id: string, damage: number, by: string) => void;
  /** Změna životů některého hráče. */
  onHp?: (id: string, hp: number) => void;
}

/**
 * Klient herního spojení.
 *
 * Drží WebSocket k Durable Objectu jedné zóny a stará se o tři věci, které
 * se v prohlížeči vždycky pokazí, když se na ně zapomene:
 *
 *  - **Odesílání stavu má vlastní takt.** Posílat pozici každý snímek by
 *    znamenalo 60 zpráv za sekundu na hráče; 12 Hz stačí, protože zbytek
 *    dopočítá interpolace na druhé straně. Navíc se neposílá nic, když se
 *    postava nehne.
 *  - **Automatické obnovení spojení** s exponenciálním odstupem. Wi-Fi
 *    výpadek nebo uspání notebooku nesmí hráče vyhodit ze hry natrvalo.
 *  - **Detekce mrtvého spojení.** TCP umí zůstat "otevřené" i když druhá
 *    strana dávno zmizela, proto heartbeat a časový limit na odpověď.
 */
export class NetworkClient {
  private socket: WebSocket | null = null;
  private status: ConnectionStatus = 'offline';
  private selfId: string | null = null;

  private readonly url: string;
  private readonly events: NetworkEvents;

  private name = '';
  private appearance: NetAppearance | null = null;

  private reconnectAttempts = 0;
  private reconnectTimer: number | null = null;
  private heartbeatTimer: number | null = null;
  private lastMessageAt = 0;
  private closedByUs = false;

  private lastSentAt = 0;
  private readonly lastSent = { x: NaN, y: NaN, z: NaN, r: NaN, a: -1 };
  private latencyMs = 0;
  private serverOffsetMs = 0;
  private clockSynced = false;

  constructor(url: string, events: NetworkEvents = {}) {
    this.url = url;
    this.events = events;
  }

  get connectionStatus(): ConnectionStatus {
    return this.status;
  }

  get id(): string | null {
    return this.selfId;
  }

  get ping(): number {
    return this.latencyMs;
  }

  /** true, jakmile známe posun serverových hodin. */
  get hasWorldClock(): boolean {
    return this.clockSynced;
  }

  /**
   * Společný čas světa v sekundách. Všichni připojení klienti dostanou
   * prakticky stejnou hodnotu, takže vidí stejné vlny i mraky.
   */
  worldTime(): number {
    const serverNow = Date.now() + this.serverOffsetMs;
    return (serverNow / 1000) % WORLD_CLOCK_PERIOD_S;
  }

  connect(name: string, appearance: NetAppearance): void {
    this.name = name;
    this.appearance = appearance;
    this.closedByUs = false;
    this.open();
  }

  disconnect(): void {
    this.closedByUs = true;
    this.clearTimers();
    this.socket?.close(1000, 'client disconnect');
    this.socket = null;
    this.setStatus('offline');
  }

  /** Pošle stav postavy. Sám si hlídá takt i to, jestli se vůbec něco změnilo. */
  sendState(x: number, y: number, z: number, rotation: number, animCode: number): void {
    if (this.status !== 'online' || !this.socket) return;

    const now = performance.now();
    if (now - this.lastSentAt < 1000 / STATE_SEND_HZ) return;

    // Prahy odpovídají tomu, co je na druhé straně vidět: pod centimetr
    // a pod zhruba půl stupně nemá smysl posílat nic.
    const moved =
      Math.abs(x - this.lastSent.x) > 0.01 ||
      Math.abs(y - this.lastSent.y) > 0.01 ||
      Math.abs(z - this.lastSent.z) > 0.01 ||
      Math.abs(rotation - this.lastSent.r) > 0.008 ||
      animCode !== this.lastSent.a;
    if (!moved) return;

    this.lastSentAt = now;
    this.lastSent.x = x;
    this.lastSent.y = y;
    this.lastSent.z = z;
    this.lastSent.r = rotation;
    this.lastSent.a = animCode;

    this.send({ t: 'st', p: [round(x), round(y), round(z)], r: round(rotation, 3), a: animCode });
  }

  sendAppearance(appearance: NetAppearance): void {
    this.appearance = appearance;
    this.send({ t: 'look', appearance });
  }

  sendChat(message: string): void {
    this.send({ t: 'chat', m: message });
  }

  /** Oznámí místo nahození, nebo null při ukončení rybaření. */
  sendSpot(p: [number, number] | null): void {
    this.send({ t: 'spot', p });
  }

  sendNpcState(state: NpcTuple[]): void {
    this.send({ t: 'npc', s: state });
  }

  sendHit(id: number, dmg: number): void {
    this.send({ t: 'hit', id, dmg });
  }

  sendPlayerDamage(id: string, dmg: number): void {
    this.send({ t: 'pdmg', id, dmg });
  }

  sendHp(hp: number): void {
    this.send({ t: 'hp', hp });
  }

  // --- vnitřní ----------------------------------------------------------

  private open(): void {
    this.clearTimers();
    this.setStatus('connecting');

    let socket: WebSocket;
    try {
      socket = new WebSocket(this.url);
    } catch (error) {
      this.setStatus('error', String(error));
      this.scheduleReconnect();
      return;
    }

    this.socket = socket;

    socket.onopen = () => {
      this.reconnectAttempts = 0;
      this.lastMessageAt = performance.now();
      this.send({
        t: 'join',
        v: PROTOCOL_VERSION,
        name: this.name,
        appearance: this.appearance!,
        pid: persistentId(),
      });
      this.startHeartbeat();
    };

    socket.onmessage = (event) => {
      this.lastMessageAt = performance.now();
      if (typeof event.data !== 'string') return;

      let message: ServerMessage;
      try {
        message = JSON.parse(event.data) as ServerMessage;
      } catch {
        return;
      }
      this.handle(message);
    };

    socket.onerror = () => {
      // onerror v prohlížeči nenese použitelný detail; podstatné je až
      // onclose, které přijde hned po něm.
      this.setStatus('error', 'chyba spojení');
    };

    socket.onclose = (event) => {
      this.clearTimers();
      this.socket = null;
      this.selfId = null;
      if (this.closedByUs) {
        this.setStatus('offline');
        return;
      }
      this.setStatus('error', event.reason || `spojení ukončeno (${event.code})`);
      this.scheduleReconnect();
    };
  }

  private handle(message: ServerMessage): void {
    switch (message.t) {
      case 'welcome':
        this.syncClock(message.serverTime);
        this.selfId = message.id;
        this.setStatus('online');
        this.events.onWelcome?.(message.id, message.players, message.resume);
        break;
      case 'spawn':
        this.events.onSpawn?.(message.player);
        break;
      case 'leave':
        this.events.onLeave?.(message.id);
        break;
      case 'st':
        this.events.onState?.(message.id, message.p, message.r, message.a, performance.now());
        break;
      case 'look':
        this.events.onLook?.(message.id, message.appearance);
        break;
      case 'chat':
        this.events.onChat?.(message.id, message.name, message.m);
        break;
      case 'spot':
        this.events.onSpot?.(message.id, message.p);
        break;
      case 'npc':
        this.events.onNpcState?.(message.from, message.s);
        break;
      case 'hit':
        this.events.onHit?.(message.id, message.dmg, message.by);
        break;
      case 'pdmg':
        this.events.onPlayerDamage?.(message.id, message.dmg, message.by);
        break;
      case 'hp':
        this.events.onHp?.(message.id, message.hp);
        break;
      case 'pong': {
        const rtt = performance.now() - message.n;
        this.latencyMs = Math.round(rtt);
        // Serverový čas platil zhruba v půlce cesty tam a zpět.
        this.syncClock(message.serverTime + rtt / 2);
        break;
      }
      case 'correct':
        // Poslední odeslaný stav byl odmítnut; zapomeneme ho, aby se filtr
        // "nic se nezměnilo" nezasekl a další pozice se poslala.
        this.lastSent.x = NaN;
        this.events.onCorrection?.(message.p, message.reason);
        break;
      case 'error':
        this.setStatus('error', message.message);
        // Neshoda verzí se opakovaným připojováním nespraví.
        if (message.code === 'version') this.closedByUs = true;
        break;
    }
  }

  private syncClock(serverTimeMs: number): void {
    const offset = serverTimeMs - Date.now();
    // První vzorek se vezme, další se průměrují — jednotlivé odezvy kolísají
    // a skokové přepisování offsetu by se projevilo trhnutím vln.
    this.serverOffsetMs = this.clockSynced ? this.serverOffsetMs * 0.8 + offset * 0.2 : offset;
    this.clockSynced = true;
  }

  private send(message: ClientMessage): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(message));
  }

  private startHeartbeat(): void {
    this.heartbeatTimer = window.setInterval(() => {
      if (performance.now() - this.lastMessageAt > CONNECTION_TIMEOUT_MS) {
        // Spojení je zombie: TCP drží, ale druhá strana mlčí.
        this.socket?.close(4000, 'timeout');
        return;
      }
      this.send({ t: 'ping', n: performance.now() });
    }, HEARTBEAT_INTERVAL_MS);
  }

  private scheduleReconnect(): void {
    if (this.closedByUs || this.reconnectTimer !== null) return;
    // Exponenciální odstup se stropem, ať se při delším výpadku serveru
    // neklepe na dveře pořád dokola.
    const delay = Math.min(15_000, 800 * 2 ** this.reconnectAttempts);
    this.reconnectAttempts++;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, delay);
  }

  private clearTimers(): void {
    if (this.heartbeatTimer !== null) window.clearInterval(this.heartbeatTimer);
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.heartbeatTimer = null;
    this.reconnectTimer = null;
  }

  private setStatus(status: ConnectionStatus, detail?: string): void {
    if (this.status === status && !detail) return;
    this.status = status;
    this.events.onStatus?.(status, detail);
  }
}

function round(value: number, decimals = 2): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/**
 * Trvalé id prohlížeče. Server podle něj pozná vracejícího se hráče a vrátí
 * ho tam, kde skončil. Není to účet ani ověření identity — jen pohodlí,
 * a taky proto se na něj nesmí vázat nic, co by šlo zneužít.
 */
function persistentId(): string {
  const KEY = 'hra.playerId';
  try {
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    // Privátní režim může localStorage zakázat — hra pojede, jen se
    // hráč po návratu objeví na výchozím bodě.
    return '';
  }
}
